import * as React from 'react';

import {
  FloatingFocusManager,
  FloatingList,
  FloatingOverlay,
  useMergeRefs,
} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {tokens, useSize} from '@packages/utilities/styled';

import {OverlayPortal} from '../../OverlayPortal';
import {View} from '../../View';
import {getDefinedValue} from '../../../utils';
import {mergeProps} from '../../../utils/mergeProps';

import {MenuNote} from '../MenuNote';
import {MenuContentContext} from '../useMenuContent';
import {useMenuContext} from '../useMenu';
import {type MenuContentProps} from '../_types';
import Mask from '../../Mask';

const X_LARGE_MENU_ITEM_HEIGHT = 26;
const MEDIUM_MENU_ITEM_HEIGHT = 24;
const X_LARGE_MENU_PADDING = 16;
const MEDIUM_MENU_PADDING = 8;

/**
 * @spring-must-be-inside Menu
 * @spring-constraint-severity error
 */
export const MenuContent = React.forwardRef<
  HTMLElement,
  MenuContentProps & React.HTMLProps<HTMLElement>
>(
  (
    {
      children,
      'aria-label': ariaLabelProp,
      'aria-labelledby': ariaLabelledByProp,
      isSeamless,
      style = emptyObject,
      onContextMenu,
      ...props
    },
    forwardedRef
  ) => {
    const {
      activeIndex,
      ariaLabel: ariaLabelContextProp,
      ariaLabelledBy: ariaLabelledByContextProp,
      context,
      elementsRef,
      floatingStyles,
      getFloatingProps,
      getItemProps,
      height,
      isNested,
      maskProps,
      minHeight: minHeightProp,
      nodeId,
      refs,
      setActiveIndex,
      shouldAllowPointerEvents,
      shouldPreventBodyScroll,
      shouldShowMask,
    } = useMenuContext();

    const [menuItemNoteHeight, setMenuItemNoteHeight] =
      React.useState<number>(0);
    const [staticMenuNoteHeight, setStaticMenuNoteHeight] =
      React.useState<number>(0);
    const [menuItemNote, setMenuItemNote] = React.useState<string | null>(null);

    const menuItemWrapperRef = React.useRef<HTMLDivElement>(null);
    const notePortalRef = React.useRef<HTMLDivElement>(null);

    const isOpen = context?.open;

    const mergedRefs = useMergeRefs([refs.setFloating, forwardedRef]);

    const ariaLabel = getDefinedValue(ariaLabelProp, ariaLabelContextProp);
    /**
     * Prefer aria-label over aria-labelledby, if defined.
     */
    const ariaLabelledBy = ariaLabel
      ? undefined
      : getDefinedValue(ariaLabelledByProp, ariaLabelledByContextProp, nodeId);

    const padding = isSeamless ? '$0' : tokens.ui.menu.content.padding;

    const mergedStyles = {
      ...style,
      ...floatingStyles,
    };

    const floatingProps = getFloatingProps();

    const handleOnContextMenu = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        // Preserve the browser's native link context menu (Open in new tab,
        // Copy link address, etc.) for MenuItems rendered as anchors.
        // Scope to this MenuContent so an ancestor <a> outside the menu does
        // not falsely skip preventDefault for non-link items.
        const target = event.target;
        const anchor =
          target instanceof Element ? target.closest('a[href]') : null;
        const isAnchorLink = Boolean(
          anchor &&
          event.currentTarget.contains(anchor) &&
          // MenuItem keeps href when disabled and only blocks left-click;
          // exclude those so "Open in new tab" cannot bypass isDisabled.
          anchor.getAttribute('aria-disabled') !== 'true'
        );
        if (!isAnchorLink) {
          event.preventDefault();
          event.stopPropagation();
        }
        onContextMenu?.(event);
      },
      [onContextMenu]
    );

    const mergedProps = mergeProps(floatingProps, props);

    const size = useSize();

    const floatingRef = refs.floating.current;

    const calculatedMinHeight = React.useMemo(() => {
      if (!floatingRef) return 0;
      if (minHeightProp) return minHeightProp;

      let maxNoteHeight = 0;
      let menuItemWrapperMinHeight =
        3 *
        (size === 'x-large'
          ? X_LARGE_MENU_ITEM_HEIGHT
          : MEDIUM_MENU_ITEM_HEIGHT);
      let menuPadding =
        size === 'x-large' ? X_LARGE_MENU_PADDING : MEDIUM_MENU_PADDING;

      if (isSeamless) {
        menuPadding = 0;
      }

      if (menuItemWrapperRef.current) {
        const heightOfMenuItemWrapper =
          menuItemWrapperRef.current.getBoundingClientRect().height;

        menuItemWrapperMinHeight = Math.min(
          heightOfMenuItemWrapper,
          menuItemWrapperMinHeight
        );
      }

      if (notePortalRef?.current) {
        if (menuItemNote) {
          maxNoteHeight = menuItemNoteHeight;
        } else if (staticMenuNoteHeight > 0) {
          maxNoteHeight = staticMenuNoteHeight;
        }
      }

      const menuContentMinHeight =
        maxNoteHeight + menuItemWrapperMinHeight + menuPadding;

      return menuContentMinHeight;
    }, [
      floatingRef,
      isSeamless,
      menuItemNote,
      menuItemNoteHeight,
      minHeightProp,
      size,
      staticMenuNoteHeight,
    ]);

    const {colorVariant: maskColorVariant = 'default', ...restMaskProps} =
      maskProps ?? emptyObject;

    const maskType =
      maskColorVariant === 'transparent' ? 'transparent' : 'popover';

    const anchorCutoutRef = refs.domReference;

    const pointerEventStyles = React.useMemo(
      () =>
        ({pointerEvents: shouldAllowPointerEvents ? 'none' : 'auto'}) as const,
      [shouldAllowPointerEvents]
    );

    return (
      <MenuContentContext.Provider
        value={{
          activeIndex,
          getItemProps,
          isOpen,
          menuItemNote,
          menuItemNoteHeight,
          notePortalRef,
          setActiveIndex,
          setStaticMenuNoteHeight,
          setMenuItemNote,
          setMenuItemNoteHeight,
          isWithinMenuContent: true,
        }}
      >
        <FloatingList elementsRef={elementsRef}>
          {isOpen && (
            <OverlayPortal>
              {/**
               * Nested Menus don't need a FloatingOverlay, the first `MenuContent` component will provide it.
               *
               * If they do mount an overlay, it will compete with the existing one and cause flickering.
               * @see https://webflow.atlassian.net/browse/DS-2106
               */}
              {!isNested ? (
                <FloatingOverlay
                  lockScroll={shouldPreventBodyScroll}
                  style={pointerEventStyles}
                />
              ) : null}
              {shouldShowMask && (
                <Mask
                  {...restMaskProps}
                  anchorCutoutRef={anchorCutoutRef}
                  /**
                   * Pointer events are not allowed when using a Mask.
                   * If a Mask is being used with Menu, the intention is to block all pointer events.
                   */
                  shouldAllowPointerEvents={false}
                  type={maskType}
                />
              )}
              <FloatingFocusManager
                context={context}
                initialFocus={isNested ? -1 : 0}
                modal={false}
              >
                <View
                  backgroundColor="$uiMenuContentBackground"
                  borderRadius="$2"
                  boxShadow="$menu"
                  data-sc="MenuContent"
                  display="flex"
                  flexDirection="column"
                  height={height}
                  maxWidth="min-content"
                  minHeight={calculatedMinHeight}
                  onContextMenu={handleOnContextMenu}
                  outline="none"
                  overflow="clip"
                  padding={padding}
                  ref={mergedRefs}
                  style={mergedStyles}
                  {...mergedProps}
                >
                  <View
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    flex="1"
                    overflowY="auto"
                    ref={menuItemWrapperRef}
                    role="menu"
                  >
                    {children}
                    {menuItemNote && (
                      <MenuNote _isShownOnHover>{menuItemNote}</MenuNote>
                    )}
                  </View>
                  {/* This div is used as a placeholder the MenuNote Portal */}
                  <div ref={notePortalRef} />
                </View>
              </FloatingFocusManager>
            </OverlayPortal>
          )}
        </FloatingList>
      </MenuContentContext.Provider>
    );
  }
);
